import { useState } from 'react';
import { formatNumber } from '../format';
import { useElementWidth } from './useElementWidth';
import { niceMax, roundedEndRect } from './chartUtils';
import { ChartTooltip, type TooltipRow } from './ChartTooltip';

export interface BarDatum {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  tooltipRows?: TooltipRow[];
}

const MARGIN = { top: 4, right: 56, bottom: 4 };
const DEFAULT_LABEL_GUTTER = 150;
/** Rough width of one character at the 11px axis-label size. */
const CHAR_WIDTH = 6.2;
const ROW_HEIGHT = 30;
const MAX_BAR_THICKNESS = 24;
const CORNER_RADIUS = 4;

/**
 * Single-series magnitude comparison. One series means one colour for every
 * bar and no legend box — the card title already names what is plotted.
 */
export function HorizontalBarChart({
  data,
  onSelect,
  labelGutter = DEFAULT_LABEL_GUTTER,
}: {
  data: BarDatum[];
  onSelect?: (key: string) => void;
  labelGutter?: number;
}) {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const [activeKey, setActiveKey] = useState<string | null>(null);

  if (data.length === 0) return <p className="empty">Nothing to show for this range.</p>;

  // Never let the gutter eat the plot on a narrow screen.
  const gutter = Math.min(labelGutter, Math.max(width * 0.45, 90));
  const maxLabelChars = Math.max(6, Math.floor((gutter - 12) / CHAR_WIDTH));
  const height = MARGIN.top + MARGIN.bottom + data.length * ROW_HEIGHT;
  const plotWidth = Math.max(width - gutter - MARGIN.right, 10);
  const max = niceMax(Math.max(...data.map((datum) => datum.value), 0));
  const barThickness = Math.min(MAX_BAR_THICKNESS, ROW_HEIGHT - 10);
  const activeIndex = data.findIndex((datum) => datum.key === activeKey);
  const active = activeIndex >= 0 ? data[activeIndex] : null;

  return (
    <div className="chart" ref={ref}>
      <svg height={height} role="img" aria-label="Call volume by row">
        {data.map((datum, index) => {
          const y = MARGIN.top + index * ROW_HEIGHT;
          const barY = y + (ROW_HEIGHT - barThickness) / 2;
          const barWidth = max > 0 ? (datum.value / max) * plotWidth : 0;
          const isActive = datum.key === activeKey;

          return (
            <g
              key={datum.key}
              tabIndex={0}
              role={onSelect ? 'button' : undefined}
              aria-label={`${datum.label}: ${formatNumber(datum.value)} calls`}
              onPointerEnter={() => setActiveKey(datum.key)}
              onPointerLeave={() => setActiveKey((current) => (current === datum.key ? null : current))}
              onFocus={() => setActiveKey(datum.key)}
              onBlur={() => setActiveKey((current) => (current === datum.key ? null : current))}
              onClick={() => onSelect?.(datum.key)}
              onKeyDown={(event) => {
                if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onSelect(datum.key);
                }
              }}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
              {/* Hit target spans the whole row, not just the painted bar. */}
              <rect
                x={0}
                y={y}
                width={Math.max(width, 1)}
                height={ROW_HEIGHT}
                fill={isActive ? 'var(--ghost)' : 'transparent'}
              />
              <text className="axis-label" x={gutter - 10} y={y + ROW_HEIGHT / 2 + 4} textAnchor="end">
                {datum.label.length > maxLabelChars
                  ? `${datum.label.slice(0, maxLabelChars - 1)}\u2026`
                  : datum.label}
              </text>
              <path
                d={roundedEndRect(gutter, barY, Math.max(barWidth, 2), barThickness, CORNER_RADIUS, 'right')}
                fill="var(--accent)"
                opacity={activeKey === null || isActive ? 1 : 0.7}
              />
              <text className="value-label" x={gutter + Math.max(barWidth, 2) + 8} y={y + ROW_HEIGHT / 2 + 4}>
                {formatNumber(datum.value)}
              </text>
            </g>
          );
        })}
        <line className="baseline" x1={gutter} x2={gutter} y1={MARGIN.top} y2={height - MARGIN.bottom} />
      </svg>

      {active ? (
        <ChartTooltip
          title={
            active.sublabel && !active.label.includes(active.sublabel)
              ? `${active.label} · ${active.sublabel}`
              : active.label
          }
          rows={active.tooltipRows ?? [{ label: 'Calls', value: formatNumber(active.value) }]}
          style={{
            left: Math.min(gutter + 12, Math.max(width - 210, 8)),
            top: MARGIN.top + activeIndex * ROW_HEIGHT + ROW_HEIGHT,
          }}
        />
      ) : null}
    </div>
  );
}
